import { Column } from 'primereact/column';
import { Dialog } from 'primereact/dialog';
import TreeNode from 'primereact/treenode';
import { TreeTable, TreeTableExpandedKeysType } from 'primereact/treetable';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  EpTreeNode,
  collectDescendantPositionIds,
  collectExpandedKeys,
  filterEpTreeForTagging,
  findEpTreeNodeByKey
} from '../../../Helper/ApiHelper/LvEpNetworkHelper';
import { EMPTY_FIELD_PLACEHOLDER } from '../../../Helper/Statics/Constants';
import BeeButton from '../../Atoms/BeeButton';
import BeeLoadingSpinner from '../../Atoms/BeeLoadingSpinner';
import BeeOutlinedButton from '../../Atoms/BeeOutlinedButton';
import BeeDropDown from '../../Atoms/BeeDropDown';
import BeeSearchBar from '../../Atoms/BeeSearchBar';
import BeeValidatedCheckbox from '../../Atoms/BeeValidatedCheckbox';
import './AlphaPhaseEpCatalogViewDialog.scss';
import {
  generateUserEpMapKey,
  isKeyUserEpMapKey
} from '../../../Helper/Util/IdGeneratorHelper';
import { KeyValEntry } from '../../../Helper/ApiHelper/KeyValueNetworkHelper';

type SelectionFilterOption = { label: string; value: string };

type CategorySelectionStaticIndex = {
  //categoryId:total Number of leaves in the category
  categoryTotals: Record<string, number>;
  //positionKey:ancestorCategories-Ids[]
  positionKeyToAncestorCategories: Record<string, string[]>;
};

/** EP row id + value for keyval / batch APIs (`'true'` / `'false'` for checkboxes). */
export type AlphaPhaseEpCatalogKeyValEntry = {
  id?: string;
  key: string;
  value: string | null;
};

type AlphaPhaseEpCatalogViewDialogProps = {
  visible: boolean;
  phaseId: string;
  header?: string;
  keyValues: KeyValEntry[] | null;
  epCatalog?: EpTreeNode[] | null;
  isLoading?: boolean;
  onHide: () => void;
  onSave?: (keyValRows: AlphaPhaseEpCatalogKeyValEntry[]) => void;
  readOnly?: boolean;
};

export default function AlphaPhaseEpCatalogViewDialog({
  visible,
  phaseId,
  header = 'EP-Katalog ansehen',
  keyValues,
  epCatalog,
  isLoading = false,
  readOnly = false,
  onHide,
  onSave
}: AlphaPhaseEpCatalogViewDialogProps) {
  const SELECTION_FILTER_ALL = '__all__';
  const [expanded, setExpanded] = useState<TreeTableExpandedKeysType>();
  const [treeSearch, setTreeSearch] = useState('');
  const [selectionFilter, setSelectionFilter] =
    useState<string>(SELECTION_FILTER_ALL);
  const [keyValRows, setKeyValRows] = useState<
    AlphaPhaseEpCatalogKeyValEntry[]
  >([]);

  const EP_CHECKED_VALUE = 'true';
  const EP_UNCHECKED_VALUE = 'false';

  /////////////////////
  //////// HOOKS //////
  /////////////////////

  const categoryIndex = useMemo(
    () => buildCategorySelectionStaticIndex(epCatalog ?? []),
    [epCatalog]
  );

  const positionCheckedMap = useMemo(() => {
    const map: Record<string, boolean> = {};
    for (const row of keyValRows) {
      map[row.key] = row.value === EP_CHECKED_VALUE;
    }
    return map;
  }, [keyValRows]);

  //list of categoryids:number of checked positions in the category
  //{357512a3-74a7-4dac-ab34-925d8f1de8e7: 11,
  //a405c8fe-71d0-482e-9ea4-330f1e439703: 11}
  const checkedByCategory = useMemo(() => {
    const counts: Record<string, number> = {};
    const tierKeyPrefix = 'user_ep_map_' + phaseId + '_';
    for (const [positionKey, checked] of Object.entries(positionCheckedMap)) {
      if (!checked) {
        continue;
      }
      const normalizedPositionKey =
        isKeyUserEpMapKey(positionKey) && positionKey.startsWith(tierKeyPrefix)
          ? positionKey.substring(tierKeyPrefix.length)
          : positionKey;

      //ancestorCategories-Ids[] for the position
      const ancestors =
        categoryIndex.positionKeyToAncestorCategories[normalizedPositionKey] ??
        [];

      for (const c of ancestors) {
        counts[c] = (counts[c] ?? 0) + 1;
      }
    }
    return counts;
  }, [phaseId, positionCheckedMap, categoryIndex]);

  const getIsPositionChecked = useCallback(
    (node: EpTreeNode): boolean => {
      return positionCheckedMap[node.key] ?? false;
    },
    [positionCheckedMap]
  );

  const filteredCatalog = useMemo(() => {
    const shouldFilterBySelection = selectionFilter !== SELECTION_FILTER_ALL;
    const positionPredicate = shouldFilterBySelection
      ? (node: EpTreeNode) => {
          const checked = getIsPositionChecked(node);
          if (selectionFilter === 'checked') {
            return checked;
          }
          if (selectionFilter === 'unchecked') {
            return !checked;
          }
          return true;
        }
      : undefined;
    return filterEpTreeForTagging(
      epCatalog ?? undefined,
      treeSearch,
      positionPredicate
    );
  }, [epCatalog, treeSearch, selectionFilter, getIsPositionChecked]);

  const selectionFilterOptions: SelectionFilterOption[] = useMemo(
    () => [
      { label: 'Alle Positionen', value: SELECTION_FILTER_ALL },
      { label: 'Ausgewählt', value: 'checked' },
      { label: 'Nicht ausgewählt', value: 'unchecked' }
    ],
    []
  );

  useEffect(() => {
    if (!keyValues || keyValues.length === 0) {
      setKeyValRows([]);
      return;
    }
    setKeyValRows(
      keyValues.map((entry) => ({
        id: entry.id,
        key: normalizeUserEpMapKey(phaseId, entry?.key),
        value:
          entry.value === null || entry.value === undefined
            ? null
            : String(entry.value)
      }))
    );
  }, [visible, keyValues, phaseId, categoryIndex]);

  useEffect(() => {
    if (visible && filteredCatalog.length > 0) {
      setExpanded(collectExpandedKeys(filteredCatalog, 2));
    } else if (visible) {
      setExpanded(undefined);
    }
  }, [visible, filteredCatalog]);

  /////////////////////
  //////// LOGIC //////
  /////////////////////

  function setDescendantsChecked(row: EpTreeNode, checked: boolean) {
    if (!epCatalog || isLoading || readOnly) {
      return;
    }
    const posIds = collectDescendantPositionIds(epCatalog, row.key);
    const updates: { positionKey: string; checked: boolean }[] = [];
    posIds.forEach((posId) => {
      const positionNode = findEpTreeNodeByKey(epCatalog, posId);
      if (!positionNode || positionNode.data.type !== 'position') {
        return;
      }
      updates.push({
        positionKey: generateUserEpMapKey(
          phaseId,
          positionNode.data.epCode ?? ''
        ),
        checked
      });
    });
    setKeyValRows((previous) =>
      updateOrInsertManyKeyValRows(phaseId, previous, updates)
    );
  }

  function handleToggleChange(positionKey: string, checked: boolean) {
    if (readOnly) {
      return;
    }
    setKeyValRows((previous) =>
      updateOrInsertKeyValRow(phaseId, previous, positionKey, checked)
    );
  }

  /** Walk once per catalog: subtree totals per category + ancestor path per position (node.key). */
  function buildCategorySelectionStaticIndex(
    nodes: EpTreeNode[] | null | undefined
  ): CategorySelectionStaticIndex {
    const categoryTotals: Record<string, number> = {};
    const positionKeyToAncestorCategories: Record<string, string[]> = {};

    function walk(list: EpTreeNode[] | null | undefined, ancestors: string[]) {
      if (!list?.length) {
        return;
      }
      for (const node of list) {
        if (!node) {
          continue;
        }
        if (node.data?.type === 'position') {
          positionKeyToAncestorCategories[node.data.epCode ?? ''] = [
            ...ancestors
          ];
          ancestors.forEach((categoryId) => {
            categoryTotals[categoryId] = (categoryTotals[categoryId] ?? 0) + 1;
          });
          continue;
        }
        if (node.children?.length) {
          walk(node.children, [...ancestors, node.key]);
        }
      }
    }

    walk(nodes ?? [], []);

    return { categoryTotals, positionKeyToAncestorCategories };
  }

  function updateOrInsertManyKeyValRows(
    phaseId: string,
    rows: AlphaPhaseEpCatalogKeyValEntry[],
    updates: { positionKey: string; checked: boolean }[]
  ): AlphaPhaseEpCatalogKeyValEntry[] {
    const map = new Map<string, AlphaPhaseEpCatalogKeyValEntry>(
      rows.map((row) => [
        normalizeUserEpMapKey(phaseId, row.key),
        {
          id: row.id,
          key: normalizeUserEpMapKey(phaseId, row.key),
          value: row.value
        }
      ])
    );
    for (const { positionKey, checked } of updates) {
      const normalizedKey = normalizeUserEpMapKey(phaseId, positionKey);
      const existingEntry = map.get(normalizedKey);
      map.set(normalizedKey, {
        id: existingEntry?.id,
        key: normalizedKey,
        value: epToggleString(checked)
      });
    }
    return Array.from(map.values());
  }

  function updateOrInsertKeyValRow(
    phaseId: string,
    rows: AlphaPhaseEpCatalogKeyValEntry[],
    key: string,
    checked: boolean
  ): AlphaPhaseEpCatalogKeyValEntry[] {
    const value = epToggleString(checked);
    const normalizedKey = normalizeUserEpMapKey(phaseId, key);
    const index = rows.findIndex(
      (r) => normalizeUserEpMapKey(phaseId, r.key) === normalizedKey
    );
    if (index === -1) {
      /** Insert new row. */
      return [...rows, { key: normalizedKey, value }];
    }
    if (rows[index].value === value) {
      /** Value is already set, do nothing. */
      return rows;
    }
    /** Update existing row. */
    const next = [...rows];
    next[index] = { id: rows[index].id, key: normalizedKey, value };
    return next;
  }
  ///////////////////////
  //////// HELPERS //////
  ///////////////////////
  function epToggleString(checked: boolean): string {
    return checked ? EP_CHECKED_VALUE : EP_UNCHECKED_VALUE;
  }

  function normalizeUserEpMapKey(phaseId: string, key: string): string {
    if (isKeyUserEpMapKey(key)) {
      return key;
    }
    return generateUserEpMapKey(phaseId, key);
  }
  ///////////////////////
  /// VIEW COMPONENTS ///
  ///////////////////////

  function renderTitleCell(node: TreeNode) {
    const data = (node as EpTreeNode).data;
    if (data.type === 'position') {
      return (
        <span>
          <i className={'pi pi-file'}></i>{' '}
          {data.number && (
            <span className={'text-sm text-color-secondary'}>
              [{data.number}]
            </span>
          )}{' '}
          <span>{data.title ?? data.shortTitle ?? '—'}</span>
        </span>
      );
    }
    return (
      <span>
        <i className={'pi pi-folder'}></i>{' '}
        {data.title ?? data.shortTitle ?? 'Kategorie'}
      </span>
    );
  }

  function renderCompletenessCell(node: TreeNode) {
    const row = node as EpTreeNode;
    const data = row.data;
    if (data.type !== 'category') {
      return null;
    }
    //categorytotals = map of categoryids:number of leaves in the category
    const totalLeaves = categoryIndex.categoryTotals[row.key] ?? 0;
    if (totalLeaves === 0) {
      return <span>{EMPTY_FIELD_PLACEHOLDER}</span>;
    }
    const checkedLeaves = checkedByCategory[row.key] ?? 0;
    const isComplete = checkedLeaves === totalLeaves;
    return (
      <span
        className={!isComplete ? '' : 'info-color font-semibold'}
      >{`${checkedLeaves}/${totalLeaves}`}</span>
    );
  }

  function renderToggleCell(node: TreeNode) {
    const entry = node as EpTreeNode;
    const data = entry.data;
    if (data.type !== 'position') {
      if (readOnly) {
        return null;
      }
      const totalLeaves = categoryIndex.categoryTotals[entry.key] ?? 0;
      const checkedLeaves = checkedByCategory[entry.key] ?? 0;
      const allChildrenChecked =
        totalLeaves > 0 && checkedLeaves === totalLeaves;
      const label = allChildrenChecked ? 'Alles abwählen' : 'Alles auswählen';
      return (
        <BeeOutlinedButton
          label={label}
          disabled={isLoading || totalLeaves === 0}
          type={allChildrenChecked ? 'primary' : 'secondary'}
          onClick={() => setDescendantsChecked(entry, !allChildrenChecked)}
        />
      );
    }
    const positionKey = entry.data.epCode ?? '';
    const positionId = generateUserEpMapKey(phaseId, positionKey);
    const checked = positionCheckedMap[positionId] ?? false;
    return (
      <div className={'flex justify-content-center'}>
        <BeeValidatedCheckbox
          label={''}
          value={checked}
          formstate={'neutral'}
          stateFalse={''}
          stateTrue={''}
          required={false}
          readOnly={isLoading || readOnly}
          onChange={(event) =>
            handleToggleChange(positionKey, event.checked ?? false)
          }
        />
      </div>
    );
  }

  function rowClassForEpTree(node: TreeNode) {
    const data = (node as EpTreeNode).data;
    return {
      'priceInput-row-category-tabIndex': data?.type === 'category',
      'priceInput-row-position-noTabIndex': data?.type === 'position'
    };
  }

  function renderTreeBody() {
    if (isLoading) {
      return (
        <div className={'ep-catalog-view-loading'}>
          <BeeLoadingSpinner strokeWidth={'3'} type={'secondary'} />
        </div>
      );
    }

    const hasFilterActive =
      (treeSearch ?? '').trim() !== '' ||
      selectionFilter !== SELECTION_FILTER_ALL;

    const treeEmptyMessage =
      (epCatalog?.length ?? 0) > 0 && filteredCatalog.length === 0
        ? hasFilterActive
          ? 'Keine Treffer für Suche oder Filter'
          : 'Keine Positionen gefunden'
        : 'Keine Positionen gefunden';

    return (
      <TreeTable
        value={filteredCatalog as unknown as TreeNode[]}
        expandedKeys={expanded}
        onToggle={(event) => setExpanded(event.value)}
        rowClassName={(rowData: TreeNode) => rowClassForEpTree(rowData)}
        className={'ep-catalog-view-table'}
        emptyMessage={treeEmptyMessage}
      >
        <Column
          header={'Bezeichnung'}
          className={'ep-catalog-view-table__title-col'}
          expander
          body={(node: TreeNode) => renderTitleCell(node)}
        />
        <Column
          header={'Auswahl'}
          className={'ep-catalog-view-table__toggle-col'}
          body={(node: TreeNode) => renderToggleCell(node)}
        />
        <Column
          header={'Anzahl'}
          className={'ep-catalog-view-table__completeness-col'}
          body={(node: TreeNode) => renderCompletenessCell(node)}
        />
      </TreeTable>
    );
  }

  function handleHide() {
    onHide();
  }

  function handleSave() {
    if (!onSave) {
      return;
    }
    onSave(keyValRows.slice());
  }

  function injectFooter() {
    if (readOnly || !onSave) {
      return (
        <div className={'flex justify-content-end gap-2'}>
          <BeeButton
            type={'primary'}
            label={'Schließen'}
            onClick={() => handleHide()}
          />
        </div>
      );
    }
    return (
      <div className={'flex justify-content-end gap-2'}>
        <BeeButton
          type={'secondary'}
          label={'Schließen'}
          disabled={isLoading}
          onClick={() => handleHide()}
        />
        <BeeButton
          type={'primary'}
          label={'Speichern'}
          disabled={isLoading}
          onClick={() => handleSave()}
        />
      </div>
    );
  }

  return (
    <Dialog
      header={header}
      visible={visible}
      modal={true}
      footer={injectFooter}
      className={
        'bee-dialog-large bee-dialog-primary alpha-phase-ep-catalog-view'
      }
      onHide={() => handleHide()}
    >
      <div className={'mb-3 mt-3'}>
        <p className={'mb-3'}>
          Bitte definieren Sie welche Leistungspositionen für den Nutzer
          angezeigt werden sollen.
        </p>
        <div className={'flex flex-wrap gap-3 align-items-end'}>
          <div className={'flex-auto'}>
            <BeeDropDown
              label={'Filter Auswahl'}
              value={selectionFilter}
              options={selectionFilterOptions}
              optionLabel={'label'}
              readOnly={false}
              disabled={isLoading}
              onChange={(value: string) =>
                setSelectionFilter(value ?? SELECTION_FILTER_ALL)
              }
            />
          </div>
          <div className={'flex-1'}>
            <BeeSearchBar
              value={treeSearch}
              disabled={isLoading}
              onSearch={(term) => setTreeSearch(term)}
            />
          </div>
        </div>
      </div>
      <div className={'mb-3'}>{renderTreeBody()}</div>
    </Dialog>
  );
}
